<template>
  <div class="barcode-scanner-wrapper p-3">
    <!-- USB ready indicator -->
    <div class="usb-indicator" :class="{ 'usb-flash': flashActive }">
      <span class="usb-dot"></span>
      ماسح USB جاهز
      <span v-if="lastScanned" class="ms-2 text-success fw-bold">{{ lastScanned }}</span>
    </div>

    <!-- Camera scan — mobile only. The decoded value is handed to
         processScanned(), the exact same pipeline USB scans go through. -->
    <button
      v-if="isMobile"
      type="button"
      class="btn btn-sm btn-outline-primary mt-2 camera-scan-btn"
      aria-label="مسح بالكاميرا"
      @click="openCamera"
    >
      <i class="pi pi-camera"></i>
      مسح بالكاميرا
    </button>

    <p v-if="errorMsg" class="text-danger mt-1 mb-0 small">{{ errorMsg }}</p>

    <Teleport to="body">
      <div v-if="cameraOpen" class="camera-sheet-backdrop" dir="rtl" @click.self="closeCamera">
        <div class="camera-sheet" role="dialog" aria-modal="true" aria-label="مسح QR / الباركود">
          <div class="camera-sheet-header">
            <h6 class="mb-0 fw-bold"><i class="pi pi-qrcode"></i> مسح QR / الباركود</h6>
            <button type="button" class="btn btn-link camera-sheet-close p-1" aria-label="إغلاق" @click="closeCamera">
              <i class="pi pi-times"></i>
            </button>
          </div>

          <div class="camera-preview">
            <div :id="scannerDivId" class="camera-reader"></div>
            <div v-if="cameraStatus === 'loading'" class="camera-overlay">
              <span class="spinner-border"></span>
              <span class="mt-2">جاري تشغيل الكاميرا...</span>
            </div>
            <div v-else-if="cameraStatus === 'error'" class="camera-overlay">
              <i class="pi pi-exclamation-triangle camera-overlay-icon"></i>
              <span class="mt-2">{{ cameraError }}</span>
              <button type="button" class="btn btn-sm btn-light mt-3" @click="startCamera">إعادة المحاولة</button>
            </div>
          </div>

          <p class="camera-hint small text-center my-2">وجّه الكاميرا نحو كود الطالب داخل الإطار</p>

          <button type="button" class="btn btn-secondary w-100" @click="closeCamera">إغلاق</button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Html5Qrcode } from 'html5-qrcode';

// Phones only: a touch-first pointer AND a phone-sized short side (portrait
// width or landscape height). Desktops (fine pointer) and tablets (short side
// >= 744px) never match, so the camera button stays hidden there.
const MOBILE_QUERY = '(pointer: coarse) and (max-width: 767.98px), (pointer: coarse) and (max-height: 500px)';

function cameraErrorMessage(err) {
  const text = String(err?.name ?? '') + ' ' + String(err?.message ?? err ?? '');
  if (/NotAllowed|Permission|denied/i.test(text)) {
    return 'تم رفض إذن الكاميرا. يرجى السماح بالوصول إلى الكاميرا من إعدادات المتصفح ثم إعادة المحاولة.';
  }
  if (/NotFound|NotReadable|Overconstrained|not supported|no camera|device/i.test(text)) {
    return 'الكاميرا غير متاحة على هذا الجهاز أو مستخدمة من تطبيق آخر.';
  }
  return 'تعذّر تشغيل الكاميرا. يرجى إعادة المحاولة.';
}

export default {
  name: 'BarcodeScanner',

  props: {
    mode: { type: String, required: true },
  },

  emits: ['scanned'],

  data() {
    return {
      isMobile: false,
      mobileMql: null,
      cameraOpen: false,
      cameraStatus: 'idle', // 'idle' | 'loading' | 'scanning' | 'error'
      cameraError: '',
      // Bumped on every start/close so a start() that resolves after the
      // sheet was closed (or restarted) knows to shut its stream down.
      cameraSession: 0,
      // Set on the first decoded frame; repeated frames of the same code are
      // ignored until the sheet is reopened.
      cameraHandled: false,
      scanner: null,
      scannerDivId: `bc-reader-${Math.random().toString(36).substr(2, 9)}`,
      errorMsg: '',
      flashActive: false,
      lastScanned: '',

      keyBuffer:   [],
      lastKeyTime: 0,
      bufferTimer: null,
      // True once a burst has been recognized as "mid-scan" (2nd+ character
      // arriving within SCAN_BURST_GAP_MS of the previous one) — used to
      // preventDefault()/blur only once per burst, never on a lone keystroke
      // that might just be normal typing.
      burstActive: false,

      lastScan: { value: null, time: 0 },
      // Duplicate-scan protection window: the SAME decoded value scanned again
      // within this many ms of its last acceptance is ignored. Applies only to
      // the completed scan value/action — never to individual USB keystrokes
      // (those are governed by SCAN_BURST_GAP_MS below). Kept at/under 1200ms
      // so a different QR — or the same one after this window — is never
      // blocked longer than that.
      DEBOUNCE_MS: 1200,

      // A USB HID keyboard-wedge scanner sends its whole payload in a few
      // milliseconds per character — far faster than any human can type.
      // MIN_SCAN_LENGTH filters out stray 1-2 key noise; SCAN_BURST_GAP_MS is
      // the max gap allowed between two keystrokes for them to still count as
      // part of the same scanner burst (well below realistic human typing
      // speed, so normal typing in a focused field never accumulates a run
      // long/fast enough to be mistaken for a scan).
      MIN_SCAN_LENGTH: 3,
      SCAN_BURST_GAP_MS: 40,
    };
  },

  methods: {
    // ── USB keyboard scanner ────────────────────────────────────────────────

    // IMPORTANT: a keyboard-wedge scanner injects keystrokes into whichever
    // element already has focus — it does not change document.activeElement.
    // This used to `return` early whenever an <input>/<textarea>/<select> was
    // focused, which silently swallowed every scan whenever the page's search
    // box (the most commonly-focused element on both Attendance and Payments)
    // had focus. That was the actual bug behind "scanner reads the code but
    // nothing happens". Detection now relies purely on TIMING (see
    // SCAN_BURST_GAP_MS/MIN_SCAN_LENGTH above), not on what currently has
    // focus, so it works regardless of where the cursor happens to be.
    handleKeydown(e) {
      const now = Date.now();

      if (e.key === 'Enter') {
        if (!this.keyBuffer.length) return; // not ours — leave Enter alone elsewhere
        e.preventDefault();
        clearTimeout(this.bufferTimer);
        const buffered = this.keyBuffer.splice(0);
        this.burstActive = false;
        if (buffered.length >= this.MIN_SCAN_LENGTH) this.processScanned(buffered.join(''));
        return;
      }

      // Reset buffer if the gap since the last key is too large to be a
      // scanner burst (i.e. this looks like normal human typing).
      if (now - this.lastKeyTime > this.SCAN_BURST_GAP_MS && this.keyBuffer.length) {
        this.keyBuffer = [];
        this.burstActive = false;
        clearTimeout(this.bufferTimer);
      }

      if (e.key.length === 1) {
        if (this.keyBuffer.length) {
          // Already mid-burst (this is at least the 2nd character): it
          // belongs to the scanner, not to whatever element happens to be
          // focused. Prevent it from being typed into a visible field (e.g.
          // the "ابحث" search box) — a leaked keystroke there triggers a live
          // search re-render/table reflow on every character of the scan,
          // which is what made the page appear to auto-scroll.
          e.preventDefault();
          if (!this.burstActive) {
            this.burstActive = true;
            const active = document.activeElement;
            if (active && active !== document.body && typeof active.blur === 'function') {
              active.blur();
            }
          }
        }

        this.keyBuffer.push(e.key);
        this.lastKeyTime = now;

        clearTimeout(this.bufferTimer);
        this.bufferTimer = setTimeout(() => {
          const buffered = this.keyBuffer.splice(0);
          this.burstActive = false;
          if (buffered.length >= this.MIN_SCAN_LENGTH) this.processScanned(buffered.join(''));
        }, 200);
      }
    },

    // ── Parse & emit ────────────────────────────────────────────────────────

    // Printed barcodes (BarcodeLabel.vue) encode "STU-<id>"; printed QR codes
    // (students_qr.vue / students_qr_print.vue) encode the bare numeric id.
    // Anything else (reservation code, reservation id, ...) is passed through
    // as `raw` for the consumer to interpret.
    processScanned(raw) {
      console.log('[BarcodeScanner] QR/barcode detected');

      const value = String(raw ?? '').trim();
      if (!value) return;

      console.log('[BarcodeScanner] Decoded value:', value);

      const stuMatch = value.match(/^STU-(\d+)$/i);
      const studentId = stuMatch ? stuMatch[1] : (/^\d+$/.test(value) ? value : null);

      const dedupeKey = value;
      const now = Date.now();
      if (dedupeKey === this.lastScan.value && now - this.lastScan.time < this.DEBOUNCE_MS) {
        console.log('[BarcodeScanner] Duplicate scan ignored (within debounce window):', value);
        return;
      }

      this.lastScan      = { value: dedupeKey, time: now };
      this.lastScanned   = studentId ? `STU-${studentId}` : value;
      this.flashActive   = true;
      setTimeout(() => { this.flashActive = false; }, 600);

      this.$emit('scanned', { raw: value, studentId, mode: this.mode });
    },

    // ── Camera ──────────────────────────────────────────────────────────────

    updateIsMobile() {
      this.isMobile = !!this.mobileMql?.matches;
      if (!this.isMobile && this.cameraOpen) this.closeCamera();
    },

    openCamera() {
      if (this.cameraOpen) return;
      this.cameraOpen = true;
      this.startCamera();
    },

    async startCamera() {
      const session = ++this.cameraSession;
      this.cameraStatus = 'loading';
      this.cameraError = '';
      this.cameraHandled = false;
      await this.$nextTick();

      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
        this.cameraStatus = 'error';
        this.cameraError = 'الكاميرا غير متاحة في هذا المتصفح. يجب فتح الصفحة عبر اتصال آمن (HTTPS).';
        return;
      }

      const scanner = new Html5Qrcode(this.scannerDivId, {
        verbose: false,
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      });
      this.scanner = scanner;
      try {
        // start() is what triggers the browser's camera permission prompt.
        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: (w, h) => {
              const size = Math.max(120, Math.floor(Math.min(w, h) * 0.7));
              return { width: size, height: size };
            },
          },
          this.onCameraDecoded,
          () => {} // frame-level decode misses are expected
        );
        if (session !== this.cameraSession) {
          try { await scanner.stop(); } catch {}
          return;
        }
        this.cameraStatus = 'scanning';
        console.log('[BarcodeScanner] Scanner initialized (camera)');
      } catch (err) {
        if (session !== this.cameraSession) return;
        if (this.scanner === scanner) this.scanner = null;
        this.cameraStatus = 'error';
        this.cameraError = cameraErrorMessage(err);
      }
    },

    async onCameraDecoded(decoded) {
      if (this.cameraHandled) return;
      this.cameraHandled = true;
      try { this.scanner?.pause(true); } catch {}
      // Close first so the page's own feedback (toasts / Swal dialogs) is not
      // hidden behind the sheet, then feed the SAME pipeline as USB scans.
      await this.closeCamera();
      this.processScanned(decoded);
    },

    async closeCamera() {
      this.cameraSession++;
      const scanner = this.scanner;
      this.scanner = null;
      if (scanner?.isScanning) {
        try { await scanner.stop(); } catch {}
      }
      this.cameraOpen = false;
      this.cameraStatus = 'idle';
      this.cameraError = '';
    },
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
    console.log('[BarcodeScanner] Scanner initialized (USB keyboard-wedge)', { mode: this.mode });

    if (typeof window.matchMedia === 'function') {
      this.mobileMql = window.matchMedia(MOBILE_QUERY);
      this.mobileMql.addEventListener?.('change', this.updateIsMobile);
      this.updateIsMobile();
    }
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
    clearTimeout(this.bufferTimer);
    this.mobileMql?.removeEventListener?.('change', this.updateIsMobile);
    this.closeCamera();
  },
};
</script>

<style scoped>
.barcode-scanner-wrapper {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
}

.usb-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #15803d;
  transition: background 0.3s;
}

.usb-indicator.usb-flash {
  background: #dcfce7;
  border-color: #22c55e;
}

.usb-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  animation: pulse-dot 1.5s infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.camera-scan-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.camera-sheet-backdrop {
  position: fixed;
  inset: 0;
  /* Above the global quick-search FAB (99999), below SweetAlert. */
  z-index: 100000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
}

.camera-sheet {
  width: 100%;
  max-width: 480px;
  max-height: 100%;
  overflow-y: auto;
  background: var(--surface-card, #fff);
  color: var(--text-primary, #212529);
  border: 1px solid var(--surface-border, transparent);
  border-bottom: 0;
  border-radius: 16px 16px 0 0;
  padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
  text-align: right;
}

.camera-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.camera-sheet-header h6,
.camera-sheet-close {
  color: inherit;
}

.camera-sheet-close {
  line-height: 1;
  text-decoration: none;
}

.camera-hint {
  color: var(--text-secondary, #6c757d);
}

.camera-preview {
  position: relative;
  min-height: 260px;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
}

.camera-reader {
  width: 100%;
}

.camera-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: #fff;
  text-align: center;
  background: #000;
}

.camera-overlay-icon {
  font-size: 2rem;
  color: #fbbf24;
}
</style>
